import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';

// List rows: aggregate query, no bank details (intentional for privacy/performance).
type SubmissionSummary = {
  id: string;
  submitted_at: string;
  full_name: string;
  email: string;
  phone: string | null;
  xero_invoice_id: string | null;
  xero_invoice_num: string | null;
  xero_status: string | null;
  pdf_r2_key: string | null;
  expense_count: number;
  total_amount: number;
  overall_status: string;
};

// Detail row: full record including bank details, fetched on expand.
type SubmissionFull = SubmissionSummary & {
  bank_account: string | null;
  bank_account_name: string | null;
  xero_contact_id: string | null;
};

type Expense = {
  id: string;
  expense_num: number;
  date: string | null;
  activity: string | null;
  supplier: string | null;
  description: string | null;
  amount: number;
  gst: string | null;
  receipt_r2_key: string | null;
  status: string;
  notes: string | null;
};

const STATUS_OPTIONS = ['Pending Review', 'Approved', 'Declined', 'On Hold', 'More Info Required'];

function statusBadge(s: string): React.ReactNode {
  const lc = s.toLowerCase();
  const bg = lc.includes('approv')
    ? '#d4edda'
    : lc.includes('declin') || lc.includes('reject')
      ? '#f8d7da'
      : lc.includes('hold')
        ? '#fff3cd'
        : lc.includes('info') || lc.includes('more')
          ? '#cce5ff'
          : '#e9ecef';
  const color = lc.includes('approv')
    ? '#155724'
    : lc.includes('declin') || lc.includes('reject')
      ? '#721c24'
      : lc.includes('hold')
        ? '#856404'
        : lc.includes('info') || lc.includes('more')
          ? '#004085'
          : '#495057';
  return (
    <span
      style={{
        background: bg,
        color,
        padding: '2px 8px',
        borderRadius: 3,
        fontWeight: 600,
        fontSize: 11,
        whiteSpace: 'nowrap',
      }}
    >
      {s}
    </span>
  );
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function fmtMoney(n: number): string {
  return `NZD ${n.toFixed(2)}`;
}

function SubmissionDetail({
  submissionId,
  onClose,
}: {
  submissionId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<{ expenses: Expense[]; submission: SubmissionFull } | null>(
    null,
  );
  const [saving, setSaving] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { status: string; notes: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const [xeroSyncError, setXeroSyncError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api
      .reimburseSubmission(submissionId)
      .then((d) => {
        setData(d as unknown as { expenses: Expense[]; submission: SubmissionFull });
        const init: Record<string, { status: string; notes: string }> = {};
        (d.expenses as Expense[]).forEach((e) => {
          init[e.id] = { status: e.status, notes: e.notes ?? '' };
        });
        setDrafts(init);
      })
      .catch((e: Error) => setError(e.message));
  }, [submissionId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (expenseId: string) => {
    const d = drafts[expenseId];
    if (!d) return;
    setSaving(expenseId);
    setError(null);
    try {
      await api.reimburseUpdateStatus(expenseId, d.status, d.notes || undefined);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(null);
    }
  };

  if (!data) {
    return (
      <div style={{ padding: 16 }}>
        {error ? (
          <p style={{ color: '#c00' }}>{error}</p>
        ) : (
          <p style={{ color: '#888' }}>Loading…</p>
        )}
      </div>
    );
  }

  const { submission: s, expenses } = data;
  const total = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: 6,
        padding: 16,
        marginTop: 12,
        background: 'var(--bg2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <strong style={{ fontSize: 14 }}>{s.full_name}</strong>
          <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{s.email}</span>
          {s.phone && <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{s.phone}</span>}
          {s.bank_account && (
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
              Bank: {s.bank_account} {s.bank_account_name ? `(${s.bank_account_name})` : ''}
            </div>
          )}
          {s.xero_invoice_id ? (
            <div style={{ fontSize: 12, marginTop: 2 }}>
              <a
                href={`https://go.xero.com/AccountsPayable/Edit.aspx?InvoiceID=${s.xero_invoice_id}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#1a3a5c' }}
              >
                Xero #{s.xero_invoice_num || s.xero_invoice_id.slice(0, 8)}
              </a>
              {s.xero_status && (
                <span style={{ color: '#888', marginLeft: 6 }}>[{s.xero_status}]</span>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              <button
                onClick={() => {
                  setXeroSyncError(null);
                  setXeroSyncing(true);
                  void api
                    .reimburseXeroSync(submissionId)
                    .then(() => load())
                    .catch((e: Error) => setXeroSyncError(e.message))
                    .finally(() => setXeroSyncing(false));
                }}
                disabled={xeroSyncing}
                style={{
                  fontSize: 12,
                  padding: '3px 10px',
                  cursor: xeroSyncing ? 'default' : 'pointer',
                  border: '1px solid #1a3a5c',
                  borderRadius: 3,
                  background: xeroSyncing ? '#aaa' : '#1a3a5c',
                  color: '#fff',
                  opacity: xeroSyncing ? 0.7 : 1,
                }}
              >
                {xeroSyncing ? 'Syncing…' : 'Sync to Xero'}
              </button>
              {xeroSyncError && (
                <p style={{ color: '#c00', fontSize: 11, margin: '3px 0 0' }}>{xeroSyncError}</p>
              )}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 600 }}>{fmtMoney(total)}</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {expenses.length} item{expenses.length !== 1 ? 's' : ''}
          </div>
          <button
            onClick={onClose}
            style={{
              marginTop: 8,
              fontSize: 12,
              padding: '2px 8px',
              cursor: 'pointer',
              border: '1px solid #ccc',
              borderRadius: 3,
              background: 'transparent',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {error && <p style={{ color: '#c00', fontSize: 13, marginTop: 8 }}>{error}</p>}

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12,
          marginTop: 14,
        }}
      >
        <thead>
          <tr style={{ background: '#1a3a5c', color: '#fff' }}>
            {[
              '#',
              'Date',
              'Category',
              'Supplier',
              'Description',
              'Amount',
              'GST',
              'Status',
              'Notes',
              '',
            ].map((h) => (
              <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {expenses.map((e) => {
            const draft = drafts[e.id] ?? { status: e.status, notes: e.notes ?? '' };
            const dirty = draft.status !== e.status || (draft.notes || '') !== (e.notes ?? '');
            return (
              <tr key={e.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '6px 8px', color: '#1a3a5c', fontWeight: 600 }}>
                  {e.expense_num}
                </td>
                <td style={{ padding: '6px 8px' }}>{e.date ?? '—'}</td>
                <td style={{ padding: '6px 8px' }}>{e.activity ?? '—'}</td>
                <td style={{ padding: '6px 8px' }}>{e.supplier ?? '—'}</td>
                <td style={{ padding: '6px 8px', maxWidth: 160, wordBreak: 'break-word' }}>
                  {e.description ?? '—'}
                </td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtMoney(e.amount)}</td>
                <td style={{ padding: '6px 8px' }}>{e.gst ?? '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <select
                    value={draft.status}
                    onChange={(ev) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [e.id]: {
                          ...(prev[e.id] ?? { status: e.status, notes: e.notes ?? '' }),
                          status: ev.target.value,
                        },
                      }))
                    }
                    style={{ fontSize: 11, padding: '2px 4px' }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input
                    type="text"
                    value={draft.notes}
                    onChange={(ev) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [e.id]: {
                          ...(prev[e.id] ?? { status: e.status, notes: e.notes ?? '' }),
                          notes: ev.target.value,
                        },
                      }))
                    }
                    placeholder="Notes"
                    style={{ fontSize: 11, padding: '2px 4px', width: 120 }}
                  />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  {dirty && (
                    <button
                      onClick={() => void save(e.id)}
                      disabled={saving === e.id}
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        cursor: 'pointer',
                        background: '#1a3a5c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        opacity: saving === e.id ? 0.6 : 1,
                      }}
                    >
                      {saving === e.id ? '…' : 'Save'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ReimbursePage() {
  const [submissions, setSubmissions] = useState<SubmissionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    void api
      .reimburseSubmissions()
      .then((d) => setSubmissions(d.submissions as SubmissionSummary[]))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: '#888' }}>Loading…</p>;
  if (error) return <p style={{ color: '#c00' }}>{error}</p>;

  const totalClaimed = submissions.reduce((s, sub) => s + sub.total_amount, 0);

  const stats = submissions.reduce(
    (acc, sub) => {
      const lc = sub.overall_status.toLowerCase();
      if (lc.includes('approv')) {
        acc.approvedCount++;
        acc.approvedAmount += sub.total_amount;
      } else if (lc.includes('declin') || lc.includes('reject')) {
        acc.declinedCount++;
      } else if (lc.includes('hold') || lc.includes('info') || lc.includes('more')) {
        acc.onHoldCount++;
      } else {
        acc.pendingCount++;
        acc.pendingAmount += sub.total_amount;
      }
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      if (new Date(sub.submitted_at).getTime() >= thirtyDaysAgo) acc.last30Count++;
      return acc;
    },
    {
      pendingCount: 0,
      pendingAmount: 0,
      approvedCount: 0,
      approvedAmount: 0,
      declinedCount: 0,
      onHoldCount: 0,
      last30Count: 0,
    },
  );

  const claimFormUrl = `${(import.meta.env.VITE_APP_BASE as string | undefined) ?? window.location.origin}/claim`;

  return (
    <div className="max-w-[1100px] mx-auto px-7 py-7">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Reimbursements</h2>
        <a
          href={claimFormUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 13,
            padding: '4px 12px',
            background: '#1a3a5c',
            color: '#fff',
            borderRadius: 4,
            textDecoration: 'none',
          }}
        >
          Open Claim Form ↗
        </a>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 16,
          margin: '16px 0',
          flexWrap: 'wrap',
        }}
      >
        <Stat
          label="Total submissions"
          value={String(submissions.length)}
          sub={`NZD ${totalClaimed.toFixed(2)} claimed`}
        />
        <Stat label="Last 30 days" value={String(stats.last30Count)} />
        <Stat
          label="Pending review"
          value={String(stats.pendingCount)}
          sub={stats.pendingAmount > 0 ? `NZD ${stats.pendingAmount.toFixed(2)}` : undefined}
          highlight="pending"
        />
        <Stat
          label="Approved"
          value={String(stats.approvedCount)}
          sub={stats.approvedAmount > 0 ? `NZD ${stats.approvedAmount.toFixed(2)}` : undefined}
          highlight="approved"
        />
        {(stats.declinedCount > 0 || stats.onHoldCount > 0) && (
          <Stat
            label="Declined / On hold"
            value={`${stats.declinedCount} / ${stats.onHoldCount}`}
            highlight="alert"
          />
        )}
      </div>

      {submissions.length === 0 ? (
        <p style={{ color: '#888', fontSize: 14 }}>No submissions yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f0f4f8', borderBottom: '2px solid #1a3a5c' }}>
              {['Submission ID', 'Date', 'Claimant', 'Items', 'Total', 'Status', 'Xero', ''].map(
                (h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#1a3a5c' }}>
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => (
              <>
                <tr
                  key={s.id}
                  style={{
                    borderBottom: '1px solid #eee',
                    background: expanded === s.id ? '#f0f4f8' : undefined,
                  }}
                >
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}>
                    {s.id}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    {fmtDate(s.submitted_at)}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div>{s.full_name}</div>
                    <div style={{ color: '#888', fontSize: 11 }}>{s.email}</div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>{s.expense_count}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {fmtMoney(s.total_amount)}
                  </td>
                  <td style={{ padding: '8px 10px' }}>{statusBadge(s.overall_status)}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {s.xero_invoice_id ? (
                      <a
                        href={`https://go.xero.com/AccountsPayable/Edit.aspx?InvoiceID=${s.xero_invoice_id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12, color: '#1a3a5c' }}
                      >
                        #{s.xero_invoice_num || (s.xero_invoice_id ?? '').slice(0, 8)}
                      </a>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 11 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <button
                      onClick={() => setExpanded((prev) => (prev === s.id ? null : s.id))}
                      style={{
                        fontSize: 12,
                        padding: '2px 10px',
                        cursor: 'pointer',
                        border: '1px solid #ccc',
                        borderRadius: 3,
                        background: 'transparent',
                      }}
                    >
                      {expanded === s.id ? 'Hide' : 'Review'}
                    </button>
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr key={`${s.id}-detail`}>
                    <td colSpan={8} style={{ padding: '0 10px 12px' }}>
                      <SubmissionDetail submissionId={s.id} onClose={() => setExpanded(null)} />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string | undefined;
  highlight?: 'approved' | 'pending' | 'alert' | undefined;
}) {
  const valueColor =
    highlight === 'approved' ? '#155724' : highlight === 'alert' ? '#721c24' : '#1a3a5c';
  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: 6,
        padding: '10px 16px',
        minWidth: 140,
      }}
    >
      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: valueColor }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
